import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StatCard from './StatCard';
import HighlightedCard from './HighlightedCard';
import { Gauge, gaugeClasses, LineChart, BarChart } from '@mui/x-charts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import ShotTable from './ShotTable';


const METERS_IN_YARD = 1.09361;

function convertValue(value, yards) {
  if (typeof value !== 'number') {
    return -1;
  }
  if (yards) {
    return value * METERS_IN_YARD;
  }
  return value;
}

export default function ShotDashboard() {
  const theme = useTheme();
  const auth = useAuth();
  const [isLoadingShotData, setIsLoadingShotData] = useState(true);
  const [sessions, setSessions] = useState();
  const [shotData, setShotData] = useState();
  const [playerOptions, setPlayerOptions] = useState([]);
  const [playerFilter, setPlayerFilter] = useState('');
  
  const [clubDistances, clubLabels] = useMemo(() => {
    if (!shotData?.clubs) {
      return [];
    }
    return [
      Object.values(shotData.clubs).map(({ average, club }) => convertValue(average || 0, true)),
      Object.values(shotData.clubs).map(({ average, club }) => club.id)
    ];
  }, [shotData?.clubs]);

  const colorPalette = [
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
  ];  


  const colorPaletteSecondary = [
    theme.palette.error.light,
    theme.palette.error.main,
    theme.palette.error.dark,
  ];


  const fetchPlayers = useCallback(async () => {
    const { data } = await auth.request('/api/stats/players');
    console.log(data);
    if (data.players) {
      setPlayerOptions(data.players);
      if (!playerFilter) {
        setPlayerFilter(data.players[0]);
      }
      // fetchData();
    }
  }, [auth, playerFilter]);

  const fetchData = useCallback(async () => {
    setIsLoadingShotData(true);
    // let fetchUrl = '/api/stats/sessions';
    // if (playerFilter) {
    //   fetchUrl += `?player=${playerFilter}`;
    // }
    // const { data } = await auth.request(fetchUrl);
    // console.log('data', data);
    // setSessions(data);


    const res = await auth.request(`/api/stats/dashboard`);
    console.log('DASHBOARD', res);
    setShotData(res.data);

    // // if (data.players?.length) {
    //   // setPlayerOptions(data.players);
    //   // if (!playerFilter) {
    //   //   setPlayerFilter(data.players[0]);
    //   // }
    // // }
    setIsLoadingShotData(false);

  }, [playerFilter, auth]);


  const handlePlayerChange = useCallback((event) => {
    const player = event.target.value;
    if (player === 'all') {
      setPlayerFilter('');
    } else {
      console.log('player', player);
      setPlayerFilter(player);
    }
  }, []);

  useEffect(() => {
    if (playerFilter) {
      console.log('playerFilter changed', playerFilter);
      fetchData();
    }
  }, [playerFilter]);
  
  useEffect(() => {
    // fetchPlayers();
    fetchData();
  }, []);

  if (isLoadingShotData) {
    return <Box><CircularProgress /></Box>;
  }

  return (
    <Box>

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
          Shot Data
        </Typography>
        <Box sx={{ mb: 3, ml: 'auto', minWidth: 200 }}>
          {/* <FormControl fullWidth>
            <InputLabel>Player</InputLabel>
            <Select label="Player" value={playerFilter || 'all'} onChange={handlePlayerChange}>
              {playerOptions.map(player => (
                <MenuItem key={player} value={player}>{player}</MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>
      </Box>

      <Grid2
        container
        spacing={2}
        columns={12}
        sx={{
          mb: (theme) => theme.spacing(2),
        }}
      >
        {/* {data.map((card, index) => (
          <Grid2 key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard {...card} />
          </Grid2>
        ))} */}

        <Grid2 size={{ xs: 12, sm: 3, lg: 3 }}>
          <StatCard
            title="Longest Drive (yards)"
            // title={
            //   <>
            //     <Typography>Longest Drive</Typography>
            //     <Typography>(By {shotData.drives.longest.player})</Typography>
            //   </>
            // }
            data={shotData?.drives?.data || []}
            value={convertValue(shotData?.drives?.longestDriveDistance, true)?.toFixed(0)}
            interval="All Time"
            trend={shotData?.drives?.trends?.averagePercentChange > 0 ? 'up' : 'down'}
            trendValue={Math.round(shotData?.drives?.trends?.averagePercentChange)}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 3, lg: 3 }}>
          <StatCard
            title="Average Driver Distance (yards)"
            data={shotData?.drives?.data || []}
            value={convertValue(shotData?.drives?.averageDistance, true).toFixed(0)}
            interval="Last 10 days"
            trend="up"
            trendValue={1}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 3, lg: 3 }}>
          <StatCard
            title="Fairways Hit"
            data={[]}
            value={`${(shotData?.drives?.fairwayPercent || 0).toFixed(1)}%`}
            interval="Last 10 rounds"
            trend="up"
            trendValue={1}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 3, lg: 3 }}>
          <StatCard
            title="Greens in Regulation"
            data={[]}
            value={`${(shotData?.drives?.fairwayPercent || 0).toFixed(1)}%`}
            interval="Last 10 rounds"
            trend="up"
            trendValue={1}
          />
        </Grid2>
        {/* <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
          <HighlightedCard />
        </Grid2> */}
        <Grid2 size={{ xs: 12, md: 6 }}>

          <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
            <CardContent>
              <Typography component="h2" variant="subtitle2" gutterBottom>
                Club Distances
              </Typography>

              <Box sx={{ width: '100%', flex: 1 }}>
                {shotData.clubs ? (
                <BarChart
                  colors={colorPalette}
                  height={250}
                  sx={{
                    '& .MuiAreaElement-series-organic': {
                      fill: "url('#organic')",
                    },
                    '& .MuiAreaElement-series-referral': {
                      fill: "url('#referral')",
                    },
                    '& .MuiAreaElement-series-direct': {
                      fill: "url('#direct')",
                    },
                  }}
        
                  xAxis={[{ scaleType: 'band', data: clubLabels}]}
                  // xAxis={[{ scaleType: 'band', data: ['group A', 'group B', 'group C'] }]}
                  series={[{
                    // data: [263, 224, 200, 180, 175, 160, 150, 134, 120, 110, 87, 85]
                    data: clubDistances
                  }]}
                />
                ) : null}
              {/* <LineChart
                xAxis={[{ data: [0, 50, 100, 150, 200, 250, 300, 350] }]}
                series={[
                  {
                    id: 'Driver',
                    label: 'Driver',
                    data: [0, 120, null, null, null, 0],
                    connectNulls: true,

                  },
                  {
                    id: '3W',
                    label: '3W',
                    data: [0, 63, null, null, null, 0],
                    connectNulls: true,
                  },
                ]}
                width={500}
                height={300}
              />           */}


              </Box>
            </CardContent>
          </Card>

        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
            <CardContent>
              <Typography component="h2" variant="subtitle2" gutterBottom>
                Accuracy
              </Typography>

              <Box sx={{ width: '100%' }}>
                <Gauge
                  height={220}
                  value={shotData?.smash?.average}
                  valueMax={1.5}
                  colors={colorPaletteSecondary}
                  sx={(theme) => ({
                    [`& .${gaugeClasses.valueText}`]: {
                      fontSize: 40,
                      fontWeight: 'bold',
                      fontFamily: theme.typography.h1.fontFamily,
                    },
                    [`& .${gaugeClasses.valueArc}`]: {
                      fill: theme.palette.secondary.main,
                    },
                    [`& .${gaugeClasses.referenceArc}`]: {
                      fill: theme.palette.text.disabled,
                    },
                  })}

                />
              </Box>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>


      {/* <ShotTable /> */}
      
    </Box>
  )
}